import { useCallback, useMemo } from 'react';

import { type SelectionOption } from '../types/selection-option.types';

export type SelectAllState = 'checked' | 'indeterminate' | 'unchecked';

/**
 * What a change attempt did. Every arm is separately announceable, which is the
 * point: a ceiling that silently truncates is the defect this type exists to
 * make impossible to ship.
 */
export type SelectionChangeOutcome =
  | { status: 'changed' }
  /** Some were added; the ceiling stopped the rest. */
  | { status: 'partial'; max: number }
  /** Nothing was added; the ceiling was already reached. */
  | { status: 'refused'; max: number }
  | { status: 'unchanged' };

export interface UseSelectionValueInput {
  value: readonly string[];
  onValueChange: (value: string[]) => void;
  maxSelected?: number;
}

export interface UseSelectionValue {
  selected: ReadonlySet<string>;
  isAtLimit: boolean;
  add: (value: string) => SelectionChangeOutcome;
  toggle: (value: string) => SelectionChangeOutcome;
  selectAllStateOf: (options: readonly SelectionOption[]) => SelectAllState;
  selectAll: (options: readonly SelectionOption[]) => SelectionChangeOutcome;
  unselectAll: (options: readonly SelectionOption[]) => SelectionChangeOutcome;
  clear: () => SelectionChangeOutcome;
}

const UNCHANGED: SelectionChangeOutcome = { status: 'unchanged' };
const CHANGED: SelectionChangeOutcome = { status: 'changed' };

/**
 * The selection algebra behind `MultiSelect` and `ServerMultiSelect`.
 *
 * One implementation for both, so add/remove/select-all/limit/tri-state cannot
 * drift between a coded-enum picker and a server-backed one — the drift that
 * made those two controls share nothing in the first place.
 *
 * Every operation derives its next value from the INCOMING `value` rather than
 * from a set held in state, so two calls in one tick cannot both start from the
 * same stale snapshot.
 */
export function useSelectionValue({
  value,
  onValueChange,
  maxSelected,
}: UseSelectionValueInput): UseSelectionValue {
  const selected = useMemo(() => new Set(value), [value]);

  const isAtLimit = maxSelected !== undefined && value.length >= maxSelected;

  const add = useCallback(
    (candidate: string): SelectionChangeOutcome => {
      if (selected.has(candidate)) return UNCHANGED;
      if (maxSelected !== undefined && value.length >= maxSelected) {
        return { status: 'refused', max: maxSelected };
      }

      onValueChange([...value, candidate]);
      return CHANGED;
    },
    [maxSelected, onValueChange, selected, value],
  );

  const toggle = useCallback(
    (candidate: string): SelectionChangeOutcome => {
      if (!selected.has(candidate)) return add(candidate);

      onValueChange(value.filter((current) => current !== candidate));
      return CHANGED;
    },
    [add, onValueChange, selected, value],
  );

  const selectAllStateOf = useCallback(
    (options: readonly SelectionOption[]): SelectAllState => {
      const selectable = options.filter((option) => option.isDisabled !== true);
      if (selectable.length === 0) return 'unchecked';

      const chosen = selectable.filter((option) => selected.has(option.value)).length;
      if (chosen === 0) return 'unchecked';

      return chosen === selectable.length ? 'checked' : 'indeterminate';
    },
    [selected],
  );

  const selectAll = useCallback(
    (options: readonly SelectionOption[]): SelectionChangeOutcome => {
      const candidates = options.filter(
        (option) => option.isDisabled !== true && !selected.has(option.value),
      );
      if (candidates.length === 0) return UNCHANGED;

      if (maxSelected === undefined) {
        onValueChange([...value, ...candidates.map((option) => option.value)]);
        return CHANGED;
      }

      const room = Math.max(maxSelected - value.length, 0);
      if (room === 0) return { status: 'refused', max: maxSelected };

      const taken = candidates.slice(0, room);
      onValueChange([...value, ...taken.map((option) => option.value)]);

      return taken.length < candidates.length ? { status: 'partial', max: maxSelected } : CHANGED;
    },
    [maxSelected, onValueChange, selected, value],
  );

  const unselectAll = useCallback(
    (options: readonly SelectionOption[]): SelectionChangeOutcome => {
      // A disabled option keeps its value: the control does not know why the
      // caller disabled it, and dropping it here would be a silent data loss on
      // the next save.
      const removable = new Set(
        options.filter((option) => option.isDisabled !== true).map((option) => option.value),
      );
      const next = value.filter((current) => !removable.has(current));
      if (next.length === value.length) return UNCHANGED;

      onValueChange(next);
      return CHANGED;
    },
    [onValueChange, value],
  );

  const clear = useCallback((): SelectionChangeOutcome => {
    if (value.length === 0) return UNCHANGED;

    // Everything, disabled included: this names the FIELD, not the visible list,
    // and refusing half of an explicit reset is worse than doing it.
    onValueChange([]);
    return CHANGED;
  }, [onValueChange, value]);

  return {
    selected,
    isAtLimit,
    add,
    toggle,
    selectAllStateOf,
    selectAll,
    unselectAll,
    clear,
  };
}
